import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { NewsItem } from "@/types/news";
import { formatViews } from "@/lib/news";

export default function FeaturedCarousel({ items }: { items: NewsItem[] }) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => setActive((i) => (i + 1) % items.length), 5000);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;

  const goPrev = () => setActive((i) => (i - 1 + items.length) % items.length);
  const goNext = () => setActive((i) => (i + 1) % items.length);

  return (
    <div className="relative h-[320px] w-full overflow-hidden rounded-lg bg-foreground-950 md:h-[420px]">
      {items.map((news, i) => (
        <Link
          key={news.id}
          to={`/detail/${news.id}`}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === active ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={news.cover}
            alt={`${news.title} - 91爆料焦点新闻`}
            title={`${news.title} - 91爆料`}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/95 via-foreground-950/45 to-transparent"></div>
          <div className="absolute bottom-0 left-0 w-full p-5 md:p-8">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-bold text-background-50">
                焦点爆料
              </span>
              <span className="rounded-md bg-background-50/15 px-2.5 py-1 text-xs font-medium text-background-100 backdrop-blur">
                {news.category} · {news.subCategory}
              </span>
            </div>
            <h2 className="max-w-3xl font-heading text-xl font-black leading-tight text-background-50 md:text-3xl">
              {news.title}
            </h2>
            <p className="mt-2 hidden max-w-2xl text-sm leading-relaxed text-background-300 md:block">
              {news.summary}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-background-400">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {news.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {news.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {formatViews(news.views)} 浏览
              </span>
            </div>
          </div>
        </Link>
      ))}

      <button
        type="button"
        onClick={goPrev}
        aria-label="上一条"
        className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-foreground-950/40 text-background-50 transition-colors hover:bg-primary-500 md:flex"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={goNext}
        aria-label="下一条"
        className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-foreground-950/40 text-background-50 transition-colors hover:bg-primary-500 md:flex"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-4 right-5 flex items-center gap-2">
        {items.map((news, i) => (
          <button
            key={news.id}
            type="button"
            onClick={() => setActive(i)}
            aria-label={`切换到第 ${i + 1} 条`}
            className={`h-1.5 cursor-pointer rounded-full transition-all ${
              i === active ? "w-6 bg-accent-500" : "w-1.5 bg-background-50/50"
            }`}
          ></button>
        ))}
      </div>
    </div>
  );
}